import { useCart } from "@/lib/cart";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { formatGBP } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, remove, subtotal } = useCart();
  const navigate = useNavigate();

  if (!drawerOpen) return null;

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-[#1A0F11]/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-[#FFF4E0] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E8D8C8]">
          <h3 className="font-serif-display text-2xl text-[#1A0F11]">Your Bag</h3>
          <button onClick={() => setDrawerOpen(false)} data-testid="cart-drawer-close" aria-label="Close cart">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {items.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif-display text-2xl text-[#1A0F11]">Your bag is empty.</p>
              <p className="text-sm text-[#5C4A4D] mt-2">Treat yourself to something exquisite.</p>
              <Link
                to="/shop"
                onClick={() => setDrawerOpen(false)}
                className="btn-outline mt-8 inline-flex"
                data-testid="cart-empty-shop"
              >
                Shop the edit
              </Link>
            </div>
          )}

          {items.map((it) => (
            <div key={it.key} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
              <img src={it.image_url} alt={it.name} className="w-24 h-28 object-cover" />
              <div className="flex-1 flex flex-col">
                <Link to={`/product/${it.slug}`} onClick={() => setDrawerOpen(false)} className="font-serif-display text-lg text-[#1A0F11] leading-tight hover:text-[#831132]">
                  {it.name}
                </Link>
                <div className="text-xs text-[#5C4A4D] mt-1 space-y-0.5">
                  {it.length && <div>Length: {it.length}</div>}
                  {it.texture && <div>Texture: {it.texture}</div>}
                  {it.color && <div>Colour: {it.color}</div>}
                </div>
                <div className="mt-auto flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2 border border-[#E8D8C8]">
                    <button onClick={() => updateQty(it.key, it.quantity - 1)} className="px-2 py-1 hover:bg-[#FDF1EC]" data-testid={`cart-qty-dec-${it.product_id}`}><Minus size={14} /></button>
                    <span className="text-sm px-2" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                    <button onClick={() => updateQty(it.key, it.quantity + 1)} className="px-2 py-1 hover:bg-[#FDF1EC]" data-testid={`cart-qty-inc-${it.product_id}`}><Plus size={14} /></button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-[#1A0F11]">{formatGBP(it.price * it.quantity)}</span>
                    <button onClick={() => remove(it.key)} className="text-[#831132] hover:text-[#6A0D28]" data-testid={`cart-remove-${it.product_id}`} aria-label="Remove"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E8D8C8] px-6 py-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="overline">Subtotal</span>
              <span className="font-serif-display text-2xl text-[#1A0F11]" data-testid="cart-subtotal">{formatGBP(subtotal)}</span>
            </div>
            <p className="text-xs text-[#5C4A4D]">Tax and shipping calculated at checkout.</p>
            <button onClick={goCheckout} className="btn-primary w-full" data-testid="cart-checkout-button">
              Secure Checkout
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
